import { useMemo } from "react";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  ChartData,
  TooltipItem,
} from "chart.js";
import { Pie } from "react-chartjs-2";
import { formatDuration } from "@/lib/format-duration";

ChartJS.register(ArcElement, Tooltip, Legend);

const COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#06B6D4", "#84CC16", "#F97316"];

interface EmployeeData {
  name: string;
  seconds: number;
  percentage: number;
}

interface EmployeePieChartProps {
  data: EmployeeData[];
}

export function EmployeePieChart({ data }: EmployeePieChartProps) {
  const chartData = useMemo(() => {
    const labels = data.map((item) => item.name);

    const chartConfig: ChartData<"pie", { value: number; percentage: number }[], string> = {
      labels,
      datasets: [
        {
          label: 'Time',
          data: data.map((item) => ({ value: item.seconds, percentage: item.percentage })),
          parsing: { key: 'value' },
          backgroundColor: labels.map((_, index) => COLORS[index % COLORS.length]),
        },
      ],
    };

    return chartConfig;
  }, [data]);

  const options = useMemo(
    () => ({
      responsive: true,
      plugins: {
        legend: { 
          position: 'bottom' as const, 
          labels: { 
            usePointStyle: true, 
            padding: 20 
          } 
        },
        tooltip: {
          callbacks: {
            label: (ctx: any) => {
              const label = ctx.label || '';
              const value = ctx.parsed || 0;
              const percentage = ctx.raw?.percentage ?? ctx.raw?.count ?? null;
              return typeof percentage === 'number' ? `${label}: ${formatDuration(value)} (${percentage.toFixed(1)}%)` : `${label}: ${formatDuration(value)}`;
            },
          },
        },
      },
    }),
    []
  );

  return (
    <div className="w-full h-64">
      <Pie data={chartData} options={options} />
    </div>
  );
}
